import { Routes, Route, Link } from 'react-router-dom';
import { AuthProvider } from '@/contexts/AuthContext';
import { AnalyticsProvider } from '@/contexts/AnalyticsContext';
import { Login, Signup, Dashboard, LinkDetail, Save, Bookmarklet } from '@/pages';
import { ProtectedRoute } from '@/components/ProtectedRoute';
import { DashboardLayout } from '@/components/layout';
import { PithLogo } from '@/components/brand';
import { TooltipProvider } from '@/components/ui/tooltip';
import { Toaster } from 'sonner';
import { ErrorBoundary } from '@/lib/sentry';

// Landing page
function Landing() {
  return (
    <div className="flex min-h-screen items-center justify-center">
      <div className="text-center">
        <PithLogo size="xl" className="justify-center" />
        <p className="mt-4 text-muted-foreground">
          AI-powered read-later service with summarization and semantic search
        </p>
        <div className="mt-6 space-x-4">
          <Link
            to="/login"
            className="inline-flex h-10 items-center justify-center rounded-md bg-primary px-8 text-sm font-medium text-primary-foreground shadow transition-colors hover:bg-primary/90"
          >
            Login
          </Link>
          <Link
            to="/signup"
            className="inline-flex h-10 items-center justify-center rounded-md border border-input bg-background px-8 text-sm font-medium shadow-sm transition-colors hover:bg-accent hover:text-accent-foreground"
          >
            Sign Up
          </Link>
        </div>
      </div>
    </div>
  );
}

// 404 page
function NotFound() {
  return (
    <div className="flex min-h-screen items-center justify-center">
      <div className="text-center">
        <h1 className="text-6xl font-bold">404</h1>
        <p className="mt-2 text-muted-foreground">Page not found</p>
        <Link
          to="/"
          className="mt-6 inline-flex h-10 items-center justify-center rounded-md bg-primary px-8 text-sm font-medium text-primary-foreground shadow transition-colors hover:bg-primary/90"
        >
          Go Home
        </Link>
      </div>
    </div>
  );
}

function ErrorFallback() {
  return (
    <div className="flex min-h-screen items-center justify-center">
      <div className="text-center">
        <h1 className="text-4xl font-bold">Something went wrong</h1>
        <p className="mt-2 text-muted-foreground">
          An unexpected error occurred. Please refresh the page.
        </p>
        <button
          onClick={() => window.location.reload()}
          className="mt-6 inline-flex h-10 items-center justify-center rounded-md bg-primary px-8 text-sm font-medium text-primary-foreground shadow transition-colors hover:bg-primary/90"
        >
          Refresh Page
        </button>
      </div>
    </div>
  );
}

function App() {
  return (
    <ErrorBoundary fallback={<ErrorFallback />}>
      <AuthProvider>
        <AnalyticsProvider>
          <TooltipProvider>
            <Routes>
              {/* Public routes */}
              <Route path="/" element={<Landing />} />

              {/* Auth routes */}
              <Route path="/login" element={<Login />} />
              <Route path="/signup" element={<Signup />} />

              {/* Protected routes with dashboard layout */}
              <Route
                element={
                  <ProtectedRoute>
                    <DashboardLayout />
                  </ProtectedRoute>
                }
              >
                <Route path="/dashboard" element={<Dashboard />} />
                <Route path="/links/:id" element={<LinkDetail />} />
                <Route path="/bookmarklet" element={<Bookmarklet />} />
              </Route>

              {/* Save popup (handles its own auth state for better UX) */}
              <Route path="/save" element={<Save />} />

              {/* 404 */}
              <Route path="*" element={<NotFound />} />
            </Routes>

            {/* Toast notifications */}
            <Toaster position="top-right" richColors />
          </TooltipProvider>
        </AnalyticsProvider>
      </AuthProvider>
    </ErrorBoundary>
  );
}

export default App;
